import {
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type AnyFrontendStage,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { useEffect, useMemo } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

import { usePostOfficeContextScope as useContextScope } from '@atlassian/post-office-context/post-office-context-scope';

import { usePostOfficeContext } from '@atlassian/post-office-context/post-office-context';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import {
	usePlacementGraphQLSubscriptionApi,
	getSubscriptionMessages,
} from '@post-office/use-placement-api/graphql-subscription';

import { usePostOfficeSearchParams } from '@post-office/use-post-office-search-params';

import { scopes } from './scopes';

import { filterExperimentalMessages } from './experimental-message-list';
import { useRovoNudgePlacementConfig } from './placement-config';

export {
	RovoNudgePlacementConfigProvider,
	type RovoNudgePlacementConfig,
} from './placement-config';

const ROVO_NUDGE_PLACEMENT_ID = 'rovo-nudge';
const NARROW_EXPOSURE_MIGRATION_GATE = 'post-office_enable_choreo-narrow-exp-migration';

const LIVE_NUDGE_DEMO_MESSAGE_DATA_FRAGMENT = `... on PostOfficeMessageRovoNudgeLiveNudgeDemoMessageData {
	__typename
	title
	description
}`;

const LIVE_AND_PERSISTED_TEST_MESSAGE_DATA_FRAGMENT = `... on PostOfficeMessageRovoNudgeLiveAndPersistedTestMessageData {
	__typename
	title
	description
}`;

const JIRA_BOARD_CATCH_UP_ROVO_NUDGE_DATA_FRAGMENT = `... on PostOfficeMessageRovoNudgeJiraBoardCatchUpRovoNudgeData {
	__typename
	title
	description
}`;

const JIRA_BOARD_BACKLOG_UPDATE_WORK_SUGGESTIONS_DATA_FRAGMENT = `... on PostOfficeMessageRovoNudgeJiraBoardBacklogUpdateWorkSuggestionsData {
	__typename
	conversationChannelId
	numberOfSuggestions
	projectKey
}`;

type PostOfficeMessageRovoNudgeLiveNudgeDemoMessageData = {
	__typename: 'PostOfficeMessageRovoNudgeLiveNudgeDemoMessageData';
	title: string;
	description: string;
};

type PostOfficeMessageRovoNudgeLiveAndPersistedTestMessageData = {
	__typename: 'PostOfficeMessageRovoNudgeLiveAndPersistedTestMessageData';
	title: string;
	description: string;
};

type PostOfficeMessageRovoNudgeJiraBoardCatchUpRovoNudgeData = {
	__typename: 'PostOfficeMessageRovoNudgeJiraBoardCatchUpRovoNudgeData';
	title: string;
	description: string;
};

type PostOfficeMessageRovoNudgeJiraBoardBacklogUpdateWorkSuggestionsData = {
	__typename: 'PostOfficeMessageRovoNudgeJiraBoardBacklogUpdateWorkSuggestionsData';
	conversationChannelId: string;
	numberOfSuggestions: number;
	projectKey?: string | null;
};

type SubscriptionMessageData =
	| PostOfficeMessageRovoNudgeLiveNudgeDemoMessageData
	| PostOfficeMessageRovoNudgeLiveAndPersistedTestMessageData
	| PostOfficeMessageRovoNudgeJiraBoardCatchUpRovoNudgeData
	| PostOfficeMessageRovoNudgeJiraBoardBacklogUpdateWorkSuggestionsData;

type RovoNudgeSubscriptionVariables = {
	activeScopes: string;
	pathname?: string;
	context?: {
		tenantId?: string;
		orgId?: string;
		locale?: string;
		subproduct?: string;
		isSiteAdmin?: boolean;
		productEdition?: string;
		product?: string;
	};
};

const doesScopeMatchPathname = (scopeName: string, pathname?: string): boolean => {
	if (!pathname) {
		return false;
	}

	const scope = scopes.find((scopeConfig) => scopeConfig.name === scopeName);
	const pathnameMatcher = scope?.when?.route?.pathname as
		| string
		| { equals?: string; matches?: string }
		| undefined;

	if (!pathnameMatcher) {
		return true;
	}
	if (typeof pathnameMatcher === 'string') {
		return pathname === pathnameMatcher;
	}
	if (typeof pathnameMatcher.equals === 'string') {
		return pathname === pathnameMatcher.equals;
	}
	if (typeof pathnameMatcher.matches === 'string') {
		try {
			return new RegExp(pathnameMatcher.matches).test(pathname);
		} catch {
			return false;
		}
	}
	return true;
};

const placementConfig: PlacementBrowserConfig = {
	placementId: ROVO_NUDGE_PLACEMENT_ID,
	options: {
		choreographer: {
			isDefaultEnabled: true,
		},
		hideInIsolatedCloud: false,
		subscription: {
			enabled: true,
		},
	},
};

const placement = { createFrontend: createPlacementFrontend(placementConfig) };

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: AnyFrontendStage;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	U
> = placement.createFrontend;

const RovoNudgeSubscriptionContent = ({
	Messages,
	variables,
}: {
	Messages: { render: AnyFrontendStage };
	variables: RovoNudgeSubscriptionVariables;
}) => {
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { clearOnSubscriptionError = false } = useRovoNudgePlacementConfig();

	// Register this effect before the subscription hook so the placement traffic event is
	// scheduled as close as possible to subscription setup. The event is intended to track
	// every aligned rovo-nudge subscription attempt; placing it after the subscription hook
	// can undercount when subscription setup effects run before this analytics effect.
	useEffect(() => {
		fireAnalyticsEvent({
			eventType: 'track',
			action: 'rendered',
			actionSubject: 'rovoNudgePlacement',
			attributes: {
				placementId: ROVO_NUDGE_PLACEMENT_ID,
				activeScope: variables.activeScopes,
				...(variables.context?.locale ? { locale: variables.context.locale } : {}),
				...(variables.context?.subproduct ? { subproduct: variables.context.subproduct } : {}),
				...(variables.context?.product ? { product: variables.context.product } : {}),
			},
		});
	}, [fireAnalyticsEvent, variables]);

	const { data: subscriptionData, error: subscriptionError } =
		usePlacementGraphQLSubscriptionApi<SubscriptionMessageData>({
			dataFragments: [
				LIVE_NUDGE_DEMO_MESSAGE_DATA_FRAGMENT,
				LIVE_AND_PERSISTED_TEST_MESSAGE_DATA_FRAGMENT,
				JIRA_BOARD_CATCH_UP_ROVO_NUDGE_DATA_FRAGMENT,
				JIRA_BOARD_BACKLOG_UPDATE_WORK_SUGGESTIONS_DATA_FRAGMENT,
			],
			variables,
		});
	useEffect(() => {
		if (clearOnSubscriptionError && subscriptionError) {
			fireAnalyticsEvent({
				eventType: 'operational',
				action: 'failed',
				actionSubject: 'rovoNudgePlacement',
				attributes: {
					placementId: ROVO_NUDGE_PLACEMENT_ID,
					activeScope: variables.activeScopes,
					...(variables.context?.product ? { product: variables.context.product } : {}),
					reason: 'subscriptionError',
				},
			});
		}
	}, [clearOnSubscriptionError, fireAnalyticsEvent, subscriptionError, variables]);

	const messages = useMemo(
		() =>
			clearOnSubscriptionError && subscriptionError
				? []
				: getSubscriptionMessages<SubscriptionMessageData>(subscriptionData),
		[clearOnSubscriptionError, subscriptionData, subscriptionError],
	);
	// Preserve backend rank while letting choreographer claim a single eligible candidate.
	const messagesToRender = useMemo(
		() =>
			FeatureGates.checkGate(NARROW_EXPOSURE_MIGRATION_GATE)
				? messages
				: filterExperimentalMessages(messages).slice(0, 1),
		[messages],
	);

	return (
		<>
			{messagesToRender.map(({ data, ...message }) =>
				data ? <Messages.render key={message.messageInstanceId} {...message} {...data} /> : null,
			)}
		</>
	);
};

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	NoProps
> = createFrontend((Messages) => () => {
	const { resetOnNavigation = false } = useRovoNudgePlacementConfig();
	const activeScopes = useContextScope({
		scopes,
		treatIntersectionAsSame: true,
	});

	const activeScopeName = activeScopes.current[0]?.name;

	const { pathname } = usePostOfficeSearchParams();
	const { tenantId, orgId, locale, subproduct, isSiteAdmin, productEdition, product } =
		usePostOfficeContext();

	const isScopePathnameAligned = useMemo(
		() => (activeScopeName ? doesScopeMatchPathname(activeScopeName, pathname) : false),
		[activeScopeName, pathname],
	);

	const variables = useMemo<RovoNudgeSubscriptionVariables | null>(
		() =>
			activeScopeName && isScopePathnameAligned
				? {
						activeScopes: activeScopeName,
						pathname,
						context: {
							tenantId,
							orgId,
							locale,
							subproduct,
							isSiteAdmin,
							productEdition,
							product,
						},
					}
				: null,
		[
			activeScopeName,
			isScopePathnameAligned,
			pathname,
			tenantId,
			orgId,
			locale,
			subproduct,
			isSiteAdmin,
			productEdition,
			product,
		],
	);

	if (!variables) {
		return null;
	}

	return (
		<RovoNudgeSubscriptionContent
			key={resetOnNavigation ? `${tenantId}:${pathname}` : undefined}
			Messages={Messages}
			variables={variables}
		/>
	);
});
